/**
 * This file is a part of Amblique's projects.
 *
 * Copyright © Amblique - All Rights Reserved.
 *
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 * Proprietary and confidential.
 */

import baseGlobal from '@amblique/base/app/theme/foundations/styles';

import {blogPageGlobalStyles} from '@amblique/king-living/app/theme/contentful/blog-page-global';
import {categoryListPageGlobalStyles} from '@amblique/king-living/app/theme/contentful/category-list-page-global';
import {homePageGlobalStyles} from '@amblique/king-living/app/theme/contentful/home-page-global';
import {productPageGlobalStyles} from '@amblique/king-living/app/theme/contentful/product-page-global';
import {searchProductListPageGlobalStyles}
  from '@amblique/king-living/app/theme/components/project/search-product-list-page';

/** Calendly widget close control — replaces assets.calendly.com close-icon.svg (white on dark overlay) */
const CALENDLY_POPUP_CLOSE_SVG_DATA_URI = `data:image/svg+xml;charset=utf-8,${encodeURIComponent(
  '<svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none"><g clip-path="url(#clip0_3052_5260)"><path d="M0.625 19.3743L19.375 0.624268" stroke="#FFFFFF" stroke-opacity="0.9" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/><path d="M19.375 19.3743L0.625 0.624268" stroke="#FFFFFF" stroke-opacity="0.9" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></g><defs><clipPath id="clip0_3052_5260"><rect width="20" height="20" fill="white"/></clipPath></defs></svg>'
)}`;

const calendlyPopupCloseStyles = {
  width: '20px',
  height: '20px',
  cursor: 'pointer',
  background: `url("${CALENDLY_POPUP_CLOSE_SVG_DATA_URI}") center / contain no-repeat`,
};

export default {
  global: {
    ...baseGlobal.global,

    '*': {
      scrollbarColor: '#8C683B #F7F5F0',
      scrollbarWidth: 'thin',
    },

    // KL-1046 — hide the Google reCAPTCHA v3 badge (bottom-right floating icon) on all pages.
    // reCAPTCHA still runs invisibly in the background. Google's terms permit hiding the badge
    // ONLY when the required disclosure ("This site is protected by reCAPTCHA and the Google
    // Privacy Policy and Terms of Service apply.") is shown in each form that uses reCAPTCHA
    // (already present in the newsletter + account-create forms). `!important` overrides the
    // inline/vendor styles Google injects on the badge element.
    // See https://developers.google.com/recaptcha/docs/faq#id-like-to-hide-the-recaptcha-badge.-what-is-allowed
    '.grecaptcha-badge': {
      visibility: 'hidden !important',
    },

    '.sr-only': {
      border: '0',
      clip: 'rect(0 0 0 0)',
      height: '1px',
      margin: '-1px 0',
      overflow: 'hidden',
      padding: '0',
      position: 'absolute',
    },

    // Skip link — `.skip-to-content--focused` toggled on focus/blur (all devices + polyfill).
    'a.skip-to-content': {
      border: 0,
      borderRadius: 'md',
      clip: 'rect(0 0 0 0)',
      fontWeight: 600,
      height: '1px',
      margin: '-1px',
      overflow: 'hidden',
      padding: 0,
      position: 'absolute',
      textDecoration: 'none',
      whiteSpace: 'nowrap',
      width: '1px',
      zIndex: 1600,
    },
    'a.skip-to-content.skip-to-content--focused, a.skip-to-content:focus-visible, a.skip-to-content[data-focus-visible-added]':
      {
        backgroundColor: 'white !important',
        boxShadow: '0 0 0 3px var(--chakra-colors-blue-500) !important',
        clip: 'auto !important',
        clipPath: 'none !important',
        color: 'var(--chakra-colors-gray-800) !important',
        height: 'auto !important',
        insetInlineStart: '24px',
        margin: '0 !important',
        outline: 'none !important',
        overflow: 'visible !important',
        padding: '16px !important',
        position: 'fixed !important',
        top: '24px !important',
        width: 'auto !important',
      },

    // Desktop PDP flyout — blocked zones: no child interaction; wrapper closes flyout on click (capture).
    '[data-flyout-frozen="true"]': {
      cursor: 'default',
      position: 'relative',
    },
    '[data-flyout-frozen="true"] *': {
      pointerEvents: 'none',
    },
    '[data-flyout-frozen="true"] button, [data-flyout-frozen="true"] input, [data-flyout-frozen="true"] select, [data-flyout-frozen="true"] textarea, [data-flyout-frozen="true"] a[href], [data-flyout-frozen="true"] label, [data-flyout-frozen="true"] [role="button"], [data-flyout-frozen="true"] [role="checkbox"], [data-flyout-frozen="true"] [role="radio"], [data-flyout-frozen="true"] [role="switch"], [data-flyout-frozen="true"] [role="link"], [data-flyout-frozen="true"] [tabindex]:not([tabindex="-1"])': {
      pointerEvents: 'auto',
    },

    // focus-visible polyfill clears box-shadow on non-keyboard focus; restore elevation inside menu
    '[data-js-focus-visible] .panel-mode-picker-menu-list:focus:not([data-focus-visible-added]):not([data-focus-visible-disabled])': {
      boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.30), 0 2px 6px 2px rgba(0, 0, 0, 0.15)',
    },
    // Native HTML typography mapping to textStyles
    // h1 => headingXL
    'h1': {
      fontSize: {base: '2rem', lg: '2.5rem'},
      fontWeight: 500,
      lineHeight: 1.2,
      fontFamily: 'heading',
    },
    // h2 => headingL (140% line height mobile + desktop per design system)
    'h2': {
      fontSize: {base: '1.5rem', lg: '2rem'},
      fontWeight: 500,
      lineHeight: 1.4,
      fontFamily: 'heading',
    },
    // h3 => heading.h3 / Label XL scale
    'h3': {
      fontSize: {base: '1.25rem', lg: '1.5rem'},
      fontWeight: 500,
      lineHeight: 1.4,
      fontFamily: 'body',
    },
    // body, p, span => bodyDefault
    'body, p, span': {
      fontSize: {base: '0.875rem', lg: '1rem'},
      fontWeight: 400,
      lineHeight: 1.4,
      fontFamily: 'body',
    },

    // Override .amb-markup styles from vendor with new typography
    '.amb-markup': {
      // h1 => headingXL
      'h1, .h1': {
        fontSize: {base: '2rem', lg: '2.5rem'},
        fontWeight: 500,
        lineHeight: 1.2, // 120%
        fontFamily: 'heading',
        mb: '0.5rem',
      },
      // h2 => headingL (32px mobile / 40px desktop — typo matrix)
      'h2, .h2': {
        fontSize: {base: '1.5rem', lg: '2rem'},
        fontWeight: 500,
        lineHeight: 1.4,
        fontFamily: 'heading',
        mb: '0.5rem',
      },
      // h3 => heading.h3 (20px / 24px)
      'h3, .h3': {
        fontSize: {base: '1.25rem', lg: '1.5rem'},
        fontWeight: 500,
        lineHeight: 1.4,
        fontFamily: 'body',
        mb: '0.5rem',
      },
      // h4 (18px / 20px)
      'h4, .h4': {
        fontSize: {base: '1.125rem', lg: '1.25rem'},
        fontWeight: 500,
        lineHeight: 1.4,
        fontFamily: 'body',
        mb: '0.5rem',
      },
      // h5 (16px / 18px)
      'h5, .h5': {
        fontSize: {base: '1rem', lg: '1.125rem'},
        fontWeight: 500,
        lineHeight: 1.4,
        fontFamily: 'body',
        mb: '0.5rem',
      },
      // h6 (14px / 16px); semibold aligns with text.label.default
      'h6, .h6': {
        fontSize: {base: '0.875rem', lg: '1rem'},
        fontWeight: 600,
        lineHeight: 1.4,
        fontFamily: 'body',
        mb: '0.5rem',
      },
      // p => bodyDefault (primary text color per Figma: rgba(27, 26, 24, 0.90))
      // minHeight ensures empty p (or p with only empty tags like <p><i></i></p>) still reserve space; no effect on p with content
      'p': {
        fontSize: {base: '0.875rem', lg: '1rem'},
        fontWeight: 400,
        lineHeight: 1.4,
        fontFamily: 'body',
        mb: '0.5rem',
        minHeight: '1em',
        color: 'brandColors.greyscale90',
      },
      // Preserve spacing for empty headings (H1–H6); minHeight follows each heading's font-size
      'h1:empty': {
        minHeight: {base: '2rem', lg: '2.5rem'},
        marginTop: 0,
        marginBottom: '0.5rem',
      },
      'h2:empty': {
        minHeight: {base: '1.5rem', lg: '2rem'},
        marginTop: 0,
        marginBottom: '0.5rem',
      },
      'h3:empty': {
        minHeight: {base: '1.25rem', lg: '1.5rem'},
        marginTop: 0,
        marginBottom: '0.5rem',
      },
      'h4:empty': {
        minHeight: {base: '1.125rem', lg: '1.25rem'},
        marginTop: 0,
        marginBottom: '0.5rem',
      },
      'h5:empty': {
        minHeight: {base: '1rem', lg: '1.125rem'},
        marginTop: 0,
        marginBottom: '0.5rem',
      },
      'h6:empty': {
        minHeight: {base: '0.875rem', lg: '1rem'},
        marginTop: 0,
        marginBottom: '0.5rem',
      },
      'a': {
        textDecoration: 'underline',
        textUnderlineOffset: '35%',
      },
      'hr': {
        my: '1rem',
      },
      // blockquote => custom style with left border
      'blockquote': {
        borderLeftWidth: '4px',
        borderLeftStyle: 'solid',
        borderLeftColor: 'primary.darkLinen', // #8C683B
        paddingLeft: {base: '24px', lg: '32px'},
        my: '1rem',
      },
      // p within blockquote inherits blockquote typography (primary text color per Figma: rgba(27, 26, 24, 0.90))
      'blockquote p': {
        fontSize: {base: '1.25rem', lg: '1.75rem'}, // 20px mobile, 28px desktop
        fontWeight: 400,
        lineHeight: {base: 1.5, lg: 1.4},
        fontFamily: 'body',
        mb: 0, // Remove bottom margin for p inside blockquote
        color: 'brandColors.greyscale90',
      },
      // sup and sub => bodyS
      'sup, sub': {
        fontSize: {base: '0.8125rem', lg: '0.75rem'}, // 13px mobile, 12px desktop
        fontWeight: 400,
        lineHeight: 1.2,
        fontFamily: 'body',
      },
    },

    '.component--split-panel-double-image-block:has(+ .component--icon-set)': {
      paddingBottom: 25,
    },

    '.amb-markup > *:empty': {
      marginBottom: '0',
    },

    '.calendly-overlay': {
      backgroundColor: 'transparent',
    },

    '.calendly-overlay .calendly-close-overlay': {
      backgroundColor: 'rgba(0, 0, 0, 0.2)',
      backdropFilter: 'blur(16px)',
      WebkitBackdropFilter: 'blur(16px)',
    },

    // Higher specificity (body + ancestors) so Calendly widget.css does not win; no !important
    'body .calendly-overlay .calendly-popup .calendly-popup-close': calendlyPopupCloseStyles,
    'body .calendly-overlay .calendly-popup-close': calendlyPopupCloseStyles,
    '@media screen and (max-width: 479px)': {
      '.chakra-container:has(.sf-product-detail-page)': {
        paddingLeft: 0,
        paddingRight: 0,
      },
      'html.pdp-config-overlay-open': {
        overflow: 'hidden',
      },
      'html.pdp-config-overlay-open [data-testid="add-to-cart-strip-mobile"]': {
        opacity: 0,
        pointerEvents: 'none',
        transform: 'translateY(100%)',
      },
      'html.pdp-config-overlay-open .chakra-container': {
        paddingLeft: 0,
        paddingRight: 0,
      },
    },
    '.chakra-container:has(.sf-product-detail-page .view-details-container .swiper-button-lock) .product-view__main-flex-container, .chakra-container:not(:has(.sf-product-detail-page .view-details-container .view-details-thumbnail-swiper-container)) .product-view__main-flex-container': {
      paddingLeft: {lg: '80px'},
    },
    '.chakra-container:has(.sf-product-detail-page .view-details-container .swiper-button-lock) .view-details-container': {
      marginLeft: {base: 0, lg: '-12px'},
    },
    '.chakra-container:not(:has(.sf-product-detail-page .view-details-container .view-details-thumbnail-swiper-container)) .view-details-container': {
      marginLeft: 0,
    },
    'body[data-image-zoom-open="true"] .configuration-selector__desktop-panel': {
      zIndex: -1,
    },
    'html[data-image-zoom-simple-pdp="true"]': {
      overflow: 'hidden',
    },
    'body[data-image-zoom-simple-pdp="true"] [data-testid="add-to-cart-strip-desktop"], body[data-image-zoom-simple-pdp="true"] [data-testid="add-to-cart-strip-mobile"]': {
      opacity: 0,
      pointerEvents: 'none',
      transform: 'translateY(100%)',
    },
    '.hero-content-wrapper .component--rich-text-block p': {
      color: 'white',
    },
    'div:not(.component--blog-listing) .component--blog-grid .blog-grid-listing__row--three-col, div:not(.component--blog-listing) .component--blog-grid .blog-grid-listing__row--twoColRight': {
      paddingBottom: {base: 'unset', md: '8px'},
    },
    '.component--blog-grid + .component--split-heading': {
      paddingTop: {base: 14, md: 20},
      paddingBottom: {base: 'unset', md: 'unset'},
    },
    '.component--split-heading + .component--media-carousel': {
      backgroundColor: 'transparent',
    },
    '.blog-grid__posts:not(.blog-grid-listing__row--three-col)': {
      paddingBottom: {base: 'unset', md: '8px'},
    },
    '.component--embed-panel .mobile-compare': {
      paddingX: {base: 'unset', md: 'unset'},
    },
    '.page--generic-page': {
      '.component--container': {
        maxWidth: '1180px',
        margin: '22px auto',
        paddingLeft: '20px',
        paddingRight: '20px',
      },
    },
    ...searchProductListPageGlobalStyles,
    ...productPageGlobalStyles,
    ...blogPageGlobalStyles,
    ...categoryListPageGlobalStyles,
    ...homePageGlobalStyles,

    '.order-confirmation-survey': {
      'bg': 'white',
      'borderColor': 'grayAlpha.10',
      'borderRadius': 'unset',
      'borderWidth': '1px',
      'maxW': '100%',
      'minW': 0,
      'overflow': 'hidden',
      'p': {base: '24px 16px', lg: '24px'},
      'width': '100%',
      '& iframe': {
        display: 'block',
        maxW: '100%',
        minH: {base: '420px', lg: '520px'},
        width: '100%',
      },
    },
    '#app-main > .component--hero-banner:first-child .hero-banner, #app-main > .component--hero-banner:first-of-type .hero-banner': {
      minHeight: {base: '632px', md: '632px', lg: '810px'},
    },
  },
};
